import React from 'react';
import { 
  Box, 
  Text, 
  VStack, 
  HStack, 
  Image, 
  useColorModeValue, 
  Button,
  Icon,
  useColorMode,
  SimpleGrid,
  Avatar,
  Badge,
  Flex
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { 
  FaArrowRight, 
  FaHandsHelping, 
  FaPrayingHands, 
  FaChurch, 
  FaUsers,
  FaCalendarAlt,
  FaCross,
  FaHeart
} from 'react-icons/fa';
import { Link as RouterLink } from 'react-router-dom';
import people1 from "../media/people1.jpg";
import person1 from "../media/person1.jpg";
import psed1 from "../media/psed1.png";
import jimmy from "../media/jimmy.jpeg";
import gael from "../media/gael.jpeg";
import bola from "../media/bola.jpeg";
import jeremiah from "../media/jeremiah.jpeg";
import lola from "../media/lola.png";
import victoria from "../media/victoria.png";
import debbie from "../media/debbie.jpeg";
import pscharles from "../media/pscharles.png";
import groupPhoto from "../media/groupPhoto.JPG";
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const About = () => {
  const { colorMode } = useColorMode();
  const isDark = colorMode === 'dark';

  // Color scheme
  const darkBlue = 'blue.400';
  const darkRed = 'red.400';
  const lightBlue = 'blue.900';
  const lightRed = 'red.800';

  // Theme colors
  const purpleColor = useColorModeValue(lightBlue, darkBlue);
  const orangeColor = useColorModeValue(lightRed, darkRed);
  const gradientFrom = useColorModeValue(lightBlue, darkBlue);
  const gradientTo = useColorModeValue(lightRed, darkRed);
  const overlayGradient = useColorModeValue(
    'linear(to-b, rgba(30, 58, 138, 0.7), rgba(153, 27, 27, 0.7))',
    'linear(to-b, rgba(96, 165, 250, 0.5), rgba(248, 113, 113, 0.5))'
  );
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const cardBg = useColorModeValue('white', 'gray.800');
  const sectionBg = useColorModeValue('gray.50', 'gray.900');

  // Animation variants
  const fadeIn = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.8 } }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const slideIn = (direction) => ({
    hidden: { opacity: 0, x: direction === 'left' ? -100 : 100 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.8 } }
  });

  // Team members data
  const teamMembers = [
    {
      name: "Pastor Ed",
      roles: ["Founder & Director"],
      bio: "With over 10 years in youth ministry, Pastor Edward founded Alpha Youth UK to reach the next generation.",
      img: psed1
    },
    {
      name: "Evangelist Jimmy",
      roles: ["Bible Teacher and Mentor", "Evangelism"],
      bio: "Bible Teacher and Mentor in Alpha Youth Leadership",
      img: jimmy
    },
    {
      name: "Minister Don Gael",
      roles: ["Welfare & Protocol", "Teaching & mentoring", "Discipleship"],
      img: gael
    },
    {
      name: "Pastor Charles",
      roles: ["Teaching & mentoring", "Discipleship"],
      img: pscharles
    },
    {
      name: "Pastor Jeremiah",
      roles: ["Evangelism"],
      bio: "'I have been appointed'",
      img: jeremiah
    },
    {
      name: "Minister Debbie",
      roles: ["Head of Music & Musicians", "Head of children"],
      bio: "Leads our evangelism efforts and community outreach programs across the UK.",
      img: debbie
    },
    {
      name: "Lola",
      roles: ["Administrator"],
      img: lola
    },
    {
      name: "Lady Jade",
      roles: ["Head Finance & Fund raising"],
    },
    {
      name: "Victoria",
      roles: ["Welfare & Protocol", "Discipleship"],
      bio: "Leads our evangelism efforts and community outreach programs across the UK.",
      img: victoria
    },
    {
      name: "Bola",
      roles: ["Head of Social Media"],
      bio: "Bola is passionate about inspiring young people through faith, helping them grow in Christ while building a legacy of service and leadership.",
      img: bola
    },
  ];

  // Milestones data
  const milestones = [
    {
      year: "2010",
      title: "Alpha Youth UK Founded",
      description: "Started with a small Bible study group of 12 young people in London.",
      icon: <FaCross />
    },
    {
      year: "2012",
      title: "First National Conference",
      description: "Hosted our first annual conference with 150 attendees from across the UK.",
      icon: <FaCalendarAlt />
    },
    {
      year: "2015",
      title: "Charity Registration",
      description: "Officially registered as a charity to expand our reach and impact.",
      icon: <FaHeart />
    },
    {
      year: "2020",
      title: "Online Ministry Launch",
      description: "Expanded our reach globally through digital platforms during the pandemic.",
      icon: <FaUsers />
    },
  ];

  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const element = document.getElementById(location.hash.substring(1));
      if (element) {
        setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      }
    }
  }, [location]);

  return (
    <VStack spacing={0} overflowX="hidden">
      {/* Hero Section */}
      <Box 
        w="100%" 
        h={{ base: "50vh", md: "60vh" }} 
        bgGradient={`linear(to-r, ${gradientFrom}, ${gradientTo})`}
        position="relative"
        overflow="hidden"
      >
        <Image
          src={groupPhoto}
          alt="Alpha Youth Group"
          objectFit="cover"
          w="100%"
          h="100%"
          opacity={isDark ? 0.5 : 0.7}
          filter={isDark ? "brightness(0.8)" : "brightness(1)"}
        />
        <Box
          position="absolute"
          top="0"
          left="0"
          w="full"
          h="full"
          bgGradient={overlayGradient}
        />
        <VStack
          position="absolute"
          top="50%"
          left="50%"
          transform="translate(-50%, -50%)"
          textAlign="center"
          spacing={4}
          w="full"
          px={4}
        >
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <VStack spacing={4}>
              <motion.div variants={fadeIn}>
                <Text
                  fontSize={{ base: "4xl", md: "5xl", lg: "6xl" }}
                  fontWeight="extrabold"
                  color="white"
                  textShadow="2px 2px 4px rgba(0,0,0,0.5)"
                >
                  OUR STORY
                </Text>
              </motion.div>
              <motion.div variants={fadeIn}>
                <Text
                  fontSize={{ base: "xl", md: "2xl" }}
                  fontWeight="semibold"
                  color="white"
                  textShadow="2px 2px 4px rgba(0,0,0,0.5)"
                  maxW="800px"
                >
                  Discover the foundation, mission, and vision of Alpha Youth UK
                </Text>
              </motion.div>
            </VStack>
          </motion.div>
        </VStack>
      </Box>

      {/* Foundation Section */}
      <Box w="full" py={20} bg={sectionBg} id="foundation">
        <Box maxW="1200px" mx="auto" px={{ base: 4, md: 8 }}>
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <HStack
              spacing={10}
              align="center"
              justify="space-between"
              flexDirection={{ base: "column", md: "row" }}
            >
              <motion.div variants={slideIn('left')}>
                <VStack
                  align={{ base: "center", md: "start" }}
                  maxW={{ base: "100%", md: "50%" }}
                  spacing={6}
                >
                  <Text 
                    fontSize={{ base: "2xl", md: "3xl" }} 
                    fontWeight="bold" 
                    color={purpleColor}
                    textAlign={{ base: "center", md: "left" }}
                  >
                    Foundation of Alpha Youth UK
                  </Text>
                  
                  <Text 
                    fontSize="lg" 
                    lineHeight="tall"
                    color={textColor}
                    textAlign={{ base: "center", md: "left" }}
                  >
                    Alpha Youth UK was established in 2010 as a Christian youth organization with a passion to reach young people across the nation. Founded by a group of dedicated ministers and youth leaders, our organization was born out of a vision to create a platform where young people could encounter Christ and grow in their faith.
                  </Text>
                  
                  <Text 
                    fontSize="lg" 
                    lineHeight="tall"
                    color={textColor}
                    textAlign={{ base: "center", md: "left" }}
                  >
                    Registered as a charity in 2010 (Charity Number: 1145081), we have been serving communities across the UK through various programs, events, and initiatives designed to empower the next generation.
                  </Text>
                </VStack>
              </motion.div>

              <motion.div variants={slideIn('right')}>
                <Box position="relative" borderRadius="xl" overflow="hidden" boxShadow="2xl">
                  <Image
                    src={person1}
                    alt="Alpha Youth Founder"
                    height={{ base: "300px", md: "500px" }}
                    objectFit="cover"
                    filter={isDark ? "brightness(0.9)" : "brightness(1)"}
                  />
                  <Box
                    position="absolute"
                    top="0"
                    left="0"
                    w="100%"
                    h="100%"
                    bgGradient={overlayGradient}
                  />
                </Box>
              </motion.div>
            </HStack>
          </motion.div>
        </Box>
      </Box>

      {/* Our Team Section */}
      <Box w="full" py={20} bg={cardBg} id="team">
        <Box maxW="1200px" mx="auto" px={{ base: 4, md: 8 }}>
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <VStack spacing={12}>
              <motion.div variants={fadeIn}>
                <Text 
                  fontSize={{ base: "3xl", md: "4xl" }} 
                  fontWeight="bold" 
                  textAlign="center"
                  bgGradient={`linear(to-r, ${gradientFrom}, ${gradientTo})`}
                  bgClip="text"
                >
                  Meet Our Team
                </Text>
                <Text 
                  fontSize={{ base: "md", md: "lg" }} 
                  color={textColor}
                  textAlign="center"
                  maxW="800px"
                  mx="auto"
                  mt={4}
                >
                  Passionate leaders dedicated to guiding the next generation in their faith journey
                </Text>
              </motion.div>

              <SimpleGrid 
                columns={{ base: 1, sm: 2, md: 3 }} 
                spacing={{ base: 6, md: 8 }}
                width="full"
              >
                {teamMembers.map((member, index) => (
                  <motion.div 
                    key={index}
                    variants={fadeIn}
                    whileHover={{ y: -5 }}
                  >
                    <Box 
                      bg={sectionBg}
                      p={6}
                      borderRadius="xl"
                      boxShadow="md"
                      h="100%"
                      textAlign="center"
                      borderTopWidth="4px"
                      borderTopColor={purpleColor}
                    >
                      <Avatar 
                        size={{ base: "xl", md: "2xl" }} 
                        src={member.img} 
                        name={member.name}
                        mb={4}
                        borderWidth="4px"
                        borderColor={orangeColor}
                      />
                      <Text fontSize={{ base: "lg", md: "xl" }} fontWeight="bold" color={purpleColor}>
                        {member.name}
                      </Text>
                      
                      <Flex 
                        wrap="wrap" 
                        justify="center" 
                        gap={2} 
                        mb={3}
                        maxW="100%"
                      >
                        {member.roles.map((role, roleIndex) => (
                          <Badge 
                            key={roleIndex}
                            colorScheme="orange" 
                            fontSize={{ base: "xs", sm: "sm", md: "md" }}
                            px={2}
                            py={1}
                            borderRadius="full"
                            whiteSpace="normal"
                            textAlign="center"
                            wordBreak="break-word"
                          >
                            {role}
                          </Badge>
                        ))}
                      </Flex>
                      
                      <Text 
                        fontSize={{ base: "sm", md: "md" }} 
                        color={textColor}
                        lineHeight="tall"
                      >
                        {member.bio}
                      </Text>
                    </Box>
                  </motion.div>
                ))}
              </SimpleGrid>
            </VStack>
          </motion.div>
        </Box>
      </Box>

      {/* Our History Section */}
      <Box w="full" py={20} bg={sectionBg} id="history">
        <Box maxW="1200px" mx="auto" px={{ base: 4, md: 8 }}>
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <HStack
              spacing={10}
              align="center"
              justify="space-between"
              flexDirection={{ base: "column-reverse", md: "row" }}
            >
              <motion.div variants={slideIn('left')}>
                <Box position="relative" borderRadius="xl" overflow="hidden" boxShadow="2xl">
                  <Image
                    src={person1}
                    alt="Alpha Youth History"
                    height={{ base: "300px", md: "500px" }}
                    objectFit="cover"
                    filter={isDark ? "brightness(0.9)" : "brightness(1)"}
                  />
                  <Box
                    position="absolute"
                    top="0"
                    left="0"
                    w="100%"
                    h="100%"
                    bgGradient={overlayGradient}
                  />
                </Box>
              </motion.div>

              <motion.div variants={slideIn('right')}>
                <VStack
                  align={{ base: "center", md: "start" }}
                  maxW={{ base: "100%", md: "50%" }}
                  spacing={8}
                >
                  <Text 
                    fontSize={{ base: "2xl", md: "3xl" }} 
                    fontWeight="bold" 
                    color={orangeColor}
                    textAlign={{ base: "center", md: "left" }}
                  >
                    Our Journey
                  </Text>
                  
                  <VStack spacing={6} align="start" w="full">
                    {milestones.map((milestone, index) => (
                      <HStack 
                        key={index}
                        align="start"
                        spacing={4}
                        p={4}
                        borderRadius="lg"
                        bg={cardBg}
                        width="full"
                        boxShadow="sm"
                        _hover={{
                          boxShadow: "md",
                          transform: "translateX(5px)",
                          transition: "all 0.3s ease"
                        }}
                      >
                        <Box 
                          p={3} 
                          bgGradient={`linear(to-b, ${gradientFrom}, ${gradientTo})`}
                          color="white"
                          borderRadius="full"
                          fontSize="xl"
                        >
                          {milestone.icon}
                        </Box>
                        <Box>
                          <Text fontWeight="bold" color={purpleColor}>
                            {milestone.year} - {milestone.title}
                          </Text>
                          <Text color={textColor}>{milestone.description}</Text>
                        </Box>
                      </HStack>
                    ))}
                  </VStack>
                </VStack>
              </motion.div>
            </HStack>
          </motion.div>
        </Box>
      </Box>

      {/* Mission & Vision Section */}
      <Box w="full" py={20} bg={cardBg} id="mission">
        <Box maxW="1200px" mx="auto" px={{ base: 4, md: 8 }}>
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <VStack spacing={12}>
              <motion.div variants={fadeIn}>
                <Text 
                  fontSize={{ base: "3xl", md: "4xl" }} 
                  fontWeight="bold" 
                  textAlign="center"
                  bgGradient={`linear(to-r, ${gradientFrom}, ${gradientTo})`}
                  bgClip="text"
                >
                  Our Mission & Vision
                </Text>
              </motion.div>

              <HStack
                spacing={10}
                align="stretch"
                justify="space-between"
                flexDirection={{ base: "column", md: "row" }}
              >
                <motion.div variants={slideIn('left')}>
                  <Box 
                    bg={sectionBg}
                    p={8}
                    borderRadius="xl"
                    boxShadow="lg"
                    h="100%"
                    borderLeftWidth="4px"
                    borderLeftColor={purpleColor}
                  >
                    <VStack spacing={6} align="center">
                      <Icon as={FaChurch} boxSize={10} color={purpleColor} />
                      <Text fontSize="2xl" fontWeight="bold" color={purpleColor}>
                        Our Vision
                      </Text>
                      <Text 
                        fontSize="lg" 
                        lineHeight="tall"
                        color={textColor}
                        textAlign="center"
                        fontStyle="italic"
                      >
                        "To reveal Jesus Christ to the youth of this nation, bring them into deeper intimacy and to empower them to influence their society in all aspects of life."
                      </Text>
                    </VStack>
                  </Box>
                </motion.div>

                <motion.div variants={slideIn('right')}>
                  <Box 
                    bg={sectionBg}
                    p={8}
                    borderRadius="xl"
                    boxShadow="lg"
                    h="100%"
                    borderLeftWidth="4px"
                    borderLeftColor={orangeColor}
                  >
                    <VStack spacing={6} align="center">
                      <Icon as={FaHandsHelping} boxSize={10} color={orangeColor} />
                      <Text fontSize="2xl" fontWeight="bold" color={orangeColor}>
                        Our Mission
                      </Text>
                      <Text 
                        fontSize="lg" 
                        lineHeight="tall"
                        color={textColor}
                        textAlign="center"
                        id="core-values"
                      >
                        Bringing the saving knowledge of our Lord Jesus Christ to young people through dynamic worship, biblical teaching, and community engagement. We aim to equip youth with spiritual tools to navigate life's challenges while positively impacting their communities.
                      </Text>
                    </VStack>
                  </Box>
                </motion.div>
              </HStack>
            </VStack>
          </motion.div>
        </Box>
      </Box>

      {/* Values Section */}
      <Box w="full" py={20} bg={sectionBg} id="values">
        <Box maxW="1200px" mx="auto" px={{ base: 4, md: 8 }}>
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <VStack spacing={12}>
              <motion.div variants={fadeIn}>
                <Text 
                  fontSize={{ base: "3xl", md: "4xl" }} 
                  fontWeight="bold" 
                  textAlign="center"
                  color={purpleColor}
                >
                  Our Core Values
                </Text>
              </motion.div>

              <HStack
                spacing={8}
                align="stretch"
                justify="center"
                flexDirection={{ base: "column", md: "row" }}
                flexWrap="wrap"
              >
                <motion.div variants={fadeIn}>
                  <Box 
                    bg={cardBg}
                    p={6}
                    borderRadius="xl"
                    boxShadow="md"
                    w={{ base: "100%", md: "300px" }}
                    h="100%"
                    borderTopWidth="4px"
                    borderTopColor={orangeColor}
                    _hover={{
                      transform: "translateY(-5px)",
                      boxShadow: "lg",
                      transition: "all 0.3s ease"
                    }}
                  >
                    <VStack spacing={4} align="center">
                      <Icon as={FaPrayingHands} boxSize={8} color={orangeColor} />
                      <Text fontSize="xl" fontWeight="bold" color={purpleColor}>
                        Spiritual Growth
                      </Text>
                      <Text 
                        fontSize="md" 
                        lineHeight="tall"
                        color={textColor}
                        textAlign="center"
                      >
                        Cultivating a strong and deep intimate relationship with the Holy Spirit through prayer, worship, and biblical study.
                      </Text>
                    </VStack>
                  </Box>
                </motion.div>

                <motion.div variants={fadeIn}>
                  <Box 
                    bg={cardBg}
                    p={6}
                    borderRadius="xl"
                    boxShadow="md"
                    w={{ base: "100%", md: "300px" }}
                    h="100%"
                    borderTopWidth="4px"
                    borderTopColor={purpleColor}
                    _hover={{
                      transform: "translateY(-5px)",
                      boxShadow: "lg",
                      transition: "all 0.3s ease"
                    }}
                  >
                    <VStack spacing={4} align="center">
                      <Icon as={FaUsers} boxSize={8} color={purpleColor} />
                      <Text fontSize="xl" fontWeight="bold" color={orangeColor}>
                        Community
                      </Text>
                      <Text 
                        fontSize="md" 
                        lineHeight="tall"
                        color={textColor}
                        textAlign="center"
                      >
                        Creating a welcoming and friendly atmosphere for everyone who comes to our meetings, fostering genuine connections.
                      </Text>
                    </VStack>
                  </Box>
                </motion.div>

                <motion.div variants={fadeIn}>
                  <Box 
                    bg={cardBg}
                    p={6}
                    borderRadius="xl"
                    boxShadow="md"
                    w={{ base: "100%", md: "300px" }}
                    h="100%"
                    borderTopWidth="4px"
                    borderTopColor={orangeColor}
                    _hover={{
                      transform: "translateY(-5px)",
                      boxShadow: "lg",
                      transition: "all 0.3s ease"
                    }}
                  >
                    <VStack spacing={4} align="center">
                      <Icon as={FaHandsHelping} boxSize={8} color={orangeColor} />
                      <Text fontSize="xl" fontWeight="bold" color={purpleColor}>
                        Evangelism
                      </Text>
                      <Text 
                        fontSize="md" 
                        lineHeight="tall"
                        color={textColor}
                        textAlign="center"
                      >
                        Maintaining a strong evangelistic ethic to engage youth with the gospel message in relevant and transformative ways.
                      </Text>
                    </VStack>
                  </Box>
                </motion.div>
              </HStack>
            </VStack>
          </motion.div>
        </Box>
      </Box>

      {/* Goals Section */}
      <Box w="full" py={20} bg={cardBg} id="goals">
        <Box maxW="1200px" mx="auto" px={{ base: 4, md: 8 }}>
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
          >
            <HStack
              spacing={10}
              align="center"
              justify="space-between"
              flexDirection={{ base: "column-reverse", md: "row" }}
            >
              <motion.div variants={slideIn('left')}>
                <Box position="relative" borderRadius="xl" overflow="hidden" boxShadow="2xl">
                  <Image
                    src={people1}
                    alt="Alpha Youth Members"
                    height={{ base: "300px", md: "500px" }}
                    objectFit="cover"
                    filter={isDark ? "brightness(0.9)" : "brightness(1)"}
                  />
                  <Box
                    position="absolute"
                    top="0"
                    left="0"
                    w="100%"
                    h="100%"
                    bgGradient={overlayGradient}
                  />
                </Box>
              </motion.div>

              <motion.div variants={slideIn('right')}>
                <VStack
                  align={{ base: "center", md: "start" }}
                  maxW={{ base: "100%", md: "50%" }}
                  spacing={6}
                >
                  <Text 
                    fontSize={{ base: "2xl", md: "3xl" }} 
                    fontWeight="bold" 
                    color={orangeColor}
                    textAlign={{ base: "center", md: "left" }}
                  >
                    Our Aims & Goals
                  </Text>
                  
                  <VStack spacing={4} align="start" w="full">
                    {[
                      "To provide a platform for young people to encounter Christ and grow in their faith",
                      "To equip youth with biblical principles for successful living",
                      "To foster a mindset of success in all endeavors through Christ",
                      "To create opportunities for youth to develop leadership skills",
                      "To address contemporary issues facing young people from a biblical perspective",
                      "To build bridges between churches for united youth ministry efforts"
                    ].map((goal, index) => (
                      <HStack key={index} align="start">
                        <Box color={purpleColor} fontSize="xl" mt={1}>•</Box>
                        <Text color={textColor}>{goal}</Text>
                      </HStack>
                    ))}
                  </VStack>
                </VStack>
              </motion.div>
            </HStack>
          </motion.div>
        </Box>
      </Box>

      {/* Call to Action */}
      <Box w="full" py={20} bgGradient={`linear(to-r, ${gradientFrom}, ${gradientTo})`}>
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
        >
          <VStack spacing={8} maxW="800px" mx="auto" px={4} textAlign="center">
            <Text 
              fontSize={{ base: "3xl", md: "4xl" }} 
              fontWeight="bold" 
              color="white"
            >
              Want to Be Part of Our Story?
            </Text>
            
            <Text 
              fontSize="xl" 
              color="whiteAlpha.900"
            >
              Whether you're a young person seeking spiritual growth or a church looking to partner with us, we'd love to connect with you.
            </Text>
            
            <HStack spacing={4} pt={4} justify="center" flexWrap="wrap">
              <Button
                as={RouterLink}
                to="/contact"
                size="lg"
                colorScheme="whiteAlpha"
                rightIcon={<FaArrowRight />}
                px={8}
                borderRadius="full"
                _hover={{ 
                  transform: "scale(1.05)",
                  bg: isDark ? "whiteAlpha.800" : "whiteAlpha.600",
                  color: isDark ? "gray.800" : "white"
                }}
                transition="all 0.2s"
              >
                Contact Us
              </Button>
              
              <Button
                as={RouterLink}
                to="/events"
                size="lg"
                variant="outline"
                color="white"
                borderColor="white"
                borderWidth="2px"
                rightIcon={<FaArrowRight />}
                px={8}
                borderRadius="full"
                _hover={{ 
                  bg: "whiteAlpha.200", 
                  transform: "scale(1.05)",
                  borderWidth: "3px"
                }}
              >
                Join an Event
              </Button>
            </HStack>

            <Text color="whiteAlpha.800" mt={8}>
              Alpha Youth UK is a registered charity in England and Wales (1145081)
            </Text>
          </VStack>
        </motion.div>
      </Box>
    </VStack>
  );
};

export default About;